import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Calendar,
  Clock,
  User,
  Building2,
  AlertCircle,
  MessageSquare,
  Activity,
  FileText,
  Edit,
  Save,
  X,
  Plus,
  Upload,
  Paperclip,
  Download,
  Trash2,
  Eye,
  ZoomIn,
  ZoomOut,
  RotateCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { Badge } from '../components/ui/badge.jsx';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar.jsx';
import { Textarea } from '../components/ui/textarea.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.jsx';
import { Separator } from '../components/ui/separator.jsx';
import { ScrollArea } from '../components/ui/scroll-area.jsx';
import { Label } from '../components/ui/label.jsx';
import { Input } from '../components/ui/input.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog.jsx';

import { 
  Ticket, 
  User as UserEntity, 
  Company, 
  Client, 
  Module,
  ChatMessage,
  TicketComment 
} from '../api/supabaseEntities.js';
import { supabase } from '../api/supabaseClient.js';
import { Core as Integrations } from '../api/supabaseIntegrations.js';

// Função utilitária para criar URLs de páginas
const createPageUrl = (pageName, params = '') => {
  const basePath = '/' + pageName.toLowerCase().replace(/ /g, '-');
  return params ? `${basePath}/${params}` : basePath;
};

// Configurações de status, prioridade, etc.
const STATUS_CONFIG = {
  open: { label: 'Aberto', color: 'bg-blue-500', textColor: 'text-blue-700', bgColor: 'bg-blue-50' },
  analysis: { label: 'Em Análise', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50' },
  in_progress: { label: 'Em Andamento', color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50' },
  resolved: { label: 'Resolvido', color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Baixa', color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50' },
  normal: { label: 'Normal', color: 'bg-blue-500', textColor: 'text-blue-700', bgColor: 'bg-blue-50' },
  high: { label: 'Alta', color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50' },
  critical: { label: 'Crítica', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50' }
};

// Função auxiliar para formatação segura de datas
const formatDateSafely = (date, formatStr = "dd/MM/yyyy 'às' HH:mm") => {
  try {
    if (!date) return 'Data não disponível';
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Data inválida';
    
    return format(dateObj, formatStr);
  } catch (error) {
    console.warn('Erro ao formatar data:', error);
    return 'Data inválida';
  }
};

// Função utilitária para obter signed URL do Supabase Storage
async function getSignedUrl(path) {
  if (!path) return null;
  // Se já for uma URL http(s), retorna direto
  if (path.startsWith('http')) return path;
  
  try {
    const { data, error } = await supabase.storage.from('uploads').createSignedUrl(path, 3600); // 1 hora
    if (error) {
      console.error('Erro ao gerar signed URL:', error);
      return null;
    }
    return data?.signedUrl || null;
  } catch (error) {
    console.error('Erro ao gerar signed URL:', error);
    return null;
  }
}

// Função para normalizar anexos (garantir que o campo 'url' seja o path do Storage)
function normalizeAttachments(attachments) {
  if (!attachments || !Array.isArray(attachments)) return [];
  return attachments.map(att => {
    // Se já tem 'path', use como url; se só tem 'url' pública, extrai o path
    if (att.path) return { ...att, url: att.path };
    if (att.url && att.url.startsWith('http')) {
      // Extrai o path relativo do publicUrl
      const match = att.url.match(/\/storage\/v1\/object\/public\/uploads\/(.+)$/);
      if (match) return { ...att, url: match[1] };
    }
    return att;
  });
}

// Componente para renderizar conteúdo de comentário com imagens interativas
const CommentImageRenderer = ({ content, onImageView, onImageDownload }) => {
  if (!content) return null;

  console.log('🖼️ CommentImageRenderer - Conteúdo recebido:', content);

  // Primeiro, verificar se há tags <img> no conteúdo
  const hasImages = content.includes('<img') || content.includes('&lt;img');
  console.log('🔍 Conteúdo contém tags de imagem:', hasImages);

  // Se não há imagens, renderizar o conteúdo diretamente
  if (!hasImages) {
    console.log('📄 Renderizando apenas texto (sem imagens)');
    return <div dangerouslySetInnerHTML={{ __html: content }} />;
  }

  // Parse do HTML para extrair imagens
  const parseContentWithImages = (htmlContent) => {
    try {
      // Decodificar entidades HTML se necessário
      let decodedContent = htmlContent
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&');

      console.log('🔧 Conteúdo decodificado:', decodedContent);

      const parser = new DOMParser();
      const doc = parser.parseFromString(decodedContent, 'text/html');
      
      // Procurar imagens tanto no body quanto em toda a estrutura
      const allImages = doc.querySelectorAll('img');
      console.log('🔍 Total de imagens encontradas:', allImages.length);
      
      if (allImages.length > 0) {
        console.log('📸 URLs das imagens encontradas:', Array.from(allImages).map(img => ({
          src: img.src || img.getAttribute('src'),
          alt: img.alt || img.getAttribute('alt')
        })));

        const elements = [];
        
        // Dividir o conteúdo em partes: texto e imagens
        const containerDiv = document.createElement('div');
        containerDiv.innerHTML = decodedContent;
        
        // Encontrar todas as imagens e substituí-las por marcadores
        const images = containerDiv.querySelectorAll('img');
        const imageData = [];
        
        images.forEach((img, index) => {
          let src = img.src || img.getAttribute('src');
          const alt = img.alt || img.getAttribute('alt') || `Imagem ${index + 1}`;
          
          // Tratar imagens base64 (data:image/...)
          if (src && src.startsWith('data:image/')) {
            console.log('📷 Imagem base64 detectada');
            // Manter a imagem base64 como está
          }
          // Tratar URLs do Supabase
          else if (src && src.includes('supabase.co/storage/v1/object/')) {
            console.log('🗄️ URL do Supabase Storage detectada');
            // Manter URL como está
          }
          // Tratar paths relativos
          else if (src && !src.startsWith('http')) {
            console.log('📁 Path relativo detectado:', src);
            // Manter path como está para processar depois
          }
          
          // Salvar dados da imagem
          imageData.push({ src, alt, index });
          
          // Substituir por marcador
          const marker = document.createElement('div');
          marker.setAttribute('data-image-marker', index);
          marker.textContent = `[IMAGEM_${index}]`;
          img.parentNode.replaceChild(marker, img);
        });

        // Dividir o conteúdo pelos marcadores
        let htmlParts = containerDiv.innerHTML;
        
        imageData.forEach((imgData, index) => {
          const parts = htmlParts.split(`[IMAGEM_${index}]`);
          
          // Adicionar texto antes da imagem
          if (parts[0] && parts[0].trim()) {
            elements.push({
              type: 'text',
              key: `text-before-${index}`,
              content: parts[0]
            });
          }
          
          // Adicionar a imagem
          elements.push({
            type: 'image',
            key: `img-${index}`,
            src: imgData.src,
            alt: imgData.alt,
            index
          });
          
          // Preparar para próxima iteração
          htmlParts = parts.slice(1).join(`[IMAGEM_${index}]`);
        });
        
        // Adicionar texto restante após última imagem
        if (htmlParts && htmlParts.trim()) {
          elements.push({
            type: 'text',
            key: 'text-final',
            content: htmlParts
          });
        }
        
        console.log('✅ Elementos processados:', elements);
        return elements;
      } else {
        // Sem imagens após parsing, retornar texto original
        return [{
          type: 'text',
          key: 'text-only',
          content: decodedContent
        }];
      }
    } catch (error) {
      console.error('❌ Erro ao processar HTML:', error);
      // Em caso de erro, retornar o conteúdo original
      return [{
        type: 'text',
        key: 'fallback',
        content: htmlContent
      }];
    }
  };

  const contentElements = parseContentWithImages(content);
  console.log('📋 Elementos finais para renderização:', contentElements);

  return (
    <div className="space-y-2">
      {contentElements.map((element) => {
        if (element.type === 'image') {
          console.log('🎨 Renderizando imagem com controles:', element);
          return (
            <div key={element.key} className="relative group inline-block my-2">
              <img 
                src={element.src} 
                alt={element.alt}
                className="max-w-md max-h-64 rounded-lg border shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => {
                  console.log('👆 Clique na imagem:', element.src);
                  onImageView(element.src, element.alt);
                }}
                onError={(e) => console.error('❌ Erro ao carregar imagem:', element.src)}
                onLoad={() => console.log('✅ Imagem carregada:', element.src)}
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-8 w-8 p-0 bg-white bg-opacity-90 hover:bg-opacity-100 shadow-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('👁️ Botão visualizar clicado:', element.src);
                      onImageView(element.src, element.alt);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-8 w-8 p-0 bg-white bg-opacity-90 hover:bg-opacity-100 shadow-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('⬇️ Botão download clicado:', element.src);
                      onImageDownload(element.src, element.alt);
                    }}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div 
              key={element.key}
              dangerouslySetInnerHTML={{ __html: element.content }}
            />
          );
        }
      })}
    </div>
  );
};

// Componente para modal de visualização de imagem
const ImageViewerModal = ({ 
  isOpen, 
  onClose, 
  imageUrl, 
  imageName, 
  onDownload 
}) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await onDownload(imageUrl, imageName);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.25));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  const handleReset = () => {
    setZoom(1);
    setRotation(0);
  };

  // Reset zoom e rotação quando modal abre
  useEffect(() => {
    if (isOpen) {
      setZoom(1);
      setRotation(0);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="flex items-center justify-between">
            <span>{imageName || 'Visualizar Imagem'}</span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomOut}
                disabled={zoom <= 0.25}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm text-gray-500 min-w-16 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomIn}
                disabled={zoom >= 3}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRotate}
              >
                <RotateCw className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
              >
                Reset
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleDownload}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <span className="animate-spin h-4 w-4 border-b-2 border-white rounded-full" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {!isDownloading && <span className="ml-2">Baixar</span>}
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-auto p-4 pt-0">
          <div className="flex items-center justify-center min-h-[500px]">
            <img
              src={imageUrl}
              alt={imageName}
              className="max-w-full max-h-full object-contain transition-transform duration-200 shadow-lg rounded-lg"
              style={{
                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                transformOrigin: 'center'
              }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Componente para item de atividade
const ActivityItem = ({ activity, isLast, onImageView, onImageDownload }) => {
  const [downloadingIndex, setDownloadingIndex] = useState(null);
  
  const getActivityIcon = (type) => {
    switch (type) {
      case 'created': return <Plus className="h-4 w-4" />;
      case 'comment': return <MessageSquare className="h-4 w-4" />;
      case 'status_change': return <Activity className="h-4 w-4" />;
      case 'priority_change': return <AlertCircle className="h-4 w-4" />;
      case 'assignment': return <User className="h-4 w-4" />;
      case 'attachment': return <Paperclip className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'created': return 'bg-blue-500';
      case 'comment': return 'bg-green-500';
      case 'status_change': return 'bg-orange-500';
      case 'priority_change': return 'bg-red-500';
      case 'assignment': return 'bg-purple-500';
      case 'attachment': return 'bg-gray-500';
      default: return 'bg-gray-400';
    }
  };

  const renderAttachments = (attachments) => {
    if (!attachments || !Array.isArray(attachments) || attachments.length === 0) return null;

    return (
      <div className="mt-3 space-y-2">
        <p className="text-sm font-medium text-gray-700">Anexos:</p>
        {attachments.map((attachment, index) => (
          <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
            <Paperclip className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-700 flex-1">{attachment.name}</span>
            <Button
              size="sm"
              variant="ghost"
              disabled={downloadingIndex === index}
              onClick={async () => {
                setDownloadingIndex(index);
                let url = attachment.url;
                try {
                  if (!url || url.startsWith('blob:')) throw new Error('Arquivo não disponível para download');
                  if (!url.startsWith('http')) {
                    url = await getSignedUrl(url);
                  }
                  if (!url) throw new Error('Não foi possível gerar o link de download');
                  // Criar link temporário para download
                  const a = document.createElement('a');
                  a.href = url;
                  a.target = '_blank';
                  a.rel = 'noopener noreferrer';
                  a.download = attachment.name || 'anexo';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                } catch (err) {
                  alert(err.message || 'Erro ao baixar anexo');
                } finally {
                  setDownloadingIndex(null);
                }
              }}
              className="h-6 w-6 p-0"
            >
              {downloadingIndex === index ? (
                <span className="animate-spin h-3 w-3 border-b-2 border-gray-500 rounded-full block"></span>
              ) : (
                <Download className="h-3 w-3" />
              )}
            </Button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex gap-3">
      {/* Timeline line */}
      <div className="flex flex-col items-center">
        <div className={`flex h-8 w-8 items-center justify-center rounded-full ${getActivityColor(activity.type)} text-white`}>
          {getActivityIcon(activity.type)}
        </div>
        {!isLast && <div className="h-full w-px bg-gray-200 mt-2" />}
      </div>
      
      {/* Content */}
      <div className="flex-1 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <Avatar className="h-6 w-6">
            <AvatarImage src={activity.user_avatar_url} />
            <AvatarFallback className="text-xs">
              {(activity.user_name || activity.user?.name || 'U').substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="font-medium text-sm">{activity.user_name || activity.user?.name || 'Usuário'}</span>
          <span className="text-sm text-gray-500">{activity.description}</span>
          <span className="text-xs text-gray-400 ml-auto">
            {formatDateSafely(activity.created_at)}
          </span>
        </div>
        
        {activity.content && (
          <div className="mt-2 p-3 bg-gray-50 rounded-md text-sm text-gray-700 activity-content">
            <CommentImageRenderer 
              content={activity.content}
              onImageView={onImageView}
              onImageDownload={onImageDownload}
            />
            
            {/* Fallback: Se o conteúdo tem imagens mas não foram detectadas, mostrar aviso */}
            {(activity.content.includes('<img') || activity.content.includes('&lt;img')) && 
             !activity.content.includes('[IMAGEM_') && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                📷 Este comentário contém imagens. Clique na imagem para visualizar em tamanho maior ou fazer download.
              </div>
            )}
          </div>
        )}
        
        {renderAttachments(activity.attachments)}
      </div>
    </div>
  );
};

export default function ViewTicket() {
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get('id');
  const navigate = useNavigate();
  
  // Estados principais
  const [ticket, setTicket] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [requester, setRequester] = useState(null);
  const [assignee, setAssignee] = useState(null);
  const [company, setCompany] = useState(null);
  const [system, setSystem] = useState(null);
  const [systemModule, setSystemModule] = useState(null);
  const [activities, setActivities] = useState([]);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Estados para edição
  const [editorHtml, setEditorHtml] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [newPriority, setNewPriority] = useState('');
  const [newAssignee, setNewAssignee] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [attachments, setAttachments] = useState([]);
  
  // Estados de loading
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  // Refs
  const commentRef = useRef(null);
  const activitiesRef = useRef(null);
  const fileInputRef = useRef(null);

  // Estados para modal de motivo/solução
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [reason, setReason] = useState('');

  // Estados para visualização de imagens
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [currentImageName, setCurrentImageName] = useState('');
  
  // Verificar se o ticket está concluído
  const isTicketCompleted = ticket && ['resolved', 'closed'].includes(ticket.status);

  // Carregar dados iniciais
  useEffect(() => {
    if (ticketId) {
      loadTicketData();
    } else {
      navigate(createPageUrl('Tickets'));
    }
  }, [ticketId]);

  // Adicionar manipulador de clique global para imagens como fallback
  useEffect(() => {
    const handleImageClick = (e) => {
      if (e.target.tagName === 'IMG' && e.target.closest('.activity-content')) {
        console.log('🖼️ Fallback: Clique global em imagem detectado:', e.target.src);
        const rect = e.target.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // Se clique foi na parte direita superior (onde estariam os controles)
        if (x > rect.width - 80 && y < 40) {
          console.log('👁️ Fallback: Simulando clique em visualizar');
          handleImageView(e.target.src, e.target.alt || 'Imagem');
        } else {
          // Clique normal na imagem - também abre visualização
          console.log('👆 Fallback: Clique normal na imagem');
          handleImageView(e.target.src, e.target.alt || 'Imagem');
        }
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('click', handleImageClick);
    return () => document.removeEventListener('click', handleImageClick);
  }, []);

  const loadTicketData = async () => {
    setIsLoading(true);
    try {
      // Carregar usuário atual
      const userData = await UserEntity.me();
      setCurrentUser(userData);
      
      // Carregar dados do ticket
      const ticketData = await Ticket.findById(ticketId);
      console.log('🎫 Dados do ticket carregados:', {
        id: ticketData.id,
        system_id: ticketData.system_id,
        system_module_id: ticketData.system_module_id,
        company_id: ticketData.company_id,
        requester_id: ticketData.requester_id,
        assigned_to: ticketData.assigned_to
      });
      setTicket(ticketData);
      setNewStatus(ticketData.status);
      setNewPriority(ticketData.priority);
      setNewAssignee(ticketData.assigned_to || '');
      
      // Carregar dados relacionados
      await Promise.all([
        loadUserData(ticketData),
        loadCompanyData(ticketData.company_id),
        loadSystemData(ticketData.system_id, userData),
        loadSystemModuleData(ticketData.system_module_id, userData),
        loadActivities(), // Agora carrega atividades reais
        loadMessages()
      ]);
      
    } catch (error) {
      console.error('Erro ao carregar ticket:', error);
      navigate(createPageUrl('Tickets'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserData = async (ticketData) => {
    try {
      // Usar os dados do criador já retornados pela RPC
      if (ticketData.creator_name) {
        setRequester({
          name: ticketData.creator_name,
          avatar_url: ticketData.creator_avatar_url,
          email: ticketData.creator_email || 'Email não disponível'
        });
      } else if (ticketData.requester_id || ticketData.created_by) {
        // Fallback para buscar dados se não estiverem disponíveis
        const requesterId = ticketData.requester_id || ticketData.created_by;
        const requesterData = await UserEntity.findById(requesterId);
        setRequester(requesterData);
      }
      
      // Usar os dados do responsável já retornados pela RPC
      if (ticketData.assigned_name) {
        setAssignee({
          name: ticketData.assigned_name,
          avatar_url: ticketData.assigned_avatar_url,
          email: ticketData.assigned_email || 'Email não disponível'
        });
      } else if (ticketData.assigned_to) {
        // Fallback para buscar dados se não estiverem disponíveis
        const assigneeData = await UserEntity.findById(ticketData.assigned_to);
        setAssignee(assigneeData);
      }
    } catch (error) {
      console.error('Erro ao carregar dados de usuários:', error);
    }
  };

  const loadCompanyData = async (companyId) => {
    if (!companyId) return;
    
    try {
      const companyData = await Company.findById(companyId);
      setCompany(companyData);
    } catch (error) {
      try {
        const clientData = await Client.findById(companyId);
        setCompany(clientData);
      } catch (clientError) {
        console.error('Erro ao carregar dados da empresa:', error);
      }
    }
  };

  const loadSystemData = async (systemId, userData = null) => {
    if (!systemId) {
      console.log('📝 Ticket sem sistema associado (system_id é null)');
      return;
    }
    
    try {
      // Usar função RPC que bypassa RLS
      const { data: systemData, error } = await supabase
        .rpc('get_system_by_id', { p_system_id: systemId });
      
      if (error) {
        console.error('❌ Erro ao carregar sistema via RPC:', error);
        return;
      }
      
      if (systemData) {
        console.log('✅ Sistema encontrado:', systemData.name);
        setSystem(systemData);
      } else {
        console.log('ℹ️ Sistema não encontrado:', systemId);
      }
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados do sistema:', error);
    }
  };

  const loadSystemModuleData = async (moduleId, userData = null) => {
    if (!moduleId) {
      console.log('📝 Ticket sem módulo associado (system_module_id é null)');
      return;
    }
    
    try {
      // Usar entidade Module para consulta direta
      const moduleData = await Module.findById(moduleId);
      
      if (moduleData) {
        console.log('✅ Módulo encontrado:', moduleData.name);
        setSystemModule(moduleData);
      } else {
        console.log('ℹ️ Módulo não encontrado ou não existe:', moduleId);
        setSystemModule(null);
      }
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados do módulo:', error);
      setSystemModule(null);
    }
  };

  // Carregar atividades reais da tabela ticket_comments
  const loadActivities = async () => {
    try {
      console.log('🔄 Carregando atividades do ticket:', ticketId);
      
      // Buscar comentários/atividades do ticket
      const commentsData = await TicketComment.list({ 
        ticket_id: ticketId 
      });
      
      console.log('📋 Atividades carregadas:', commentsData);
      
      // Mapear comentários para formato de atividades
      const mappedActivities = commentsData.map(comment => ({
        id: comment.id,
        type: comment.is_solution ? 'solution' : 'comment',
        description: comment.is_solution ? 'Marcou como solução' : 'Adicionou um comentário',
        user_name: comment.user_name,
        user_avatar_url: comment.user_avatar_url,
        created_at: comment.created_at,
        content: comment.content,
        attachments: normalizeAttachments(comment.attachments || []),
        is_internal: comment.is_internal,
        time_spent: comment.time_spent
      }));
      
      setActivities(mappedActivities);
      
    } catch (error) {
      console.error('❌ Erro ao carregar atividades:', error);
      setActivities([]);
    }
  };

  const loadMessages = async () => {
    try {
      // Mapear os campos corretamente
      const messagesData = await ChatMessage.list({ 
        room_id: ticketId 
      });
      
      const formattedMessages = Array.isArray(messagesData) 
        ? messagesData.map(msg => ({
            ...msg,
            content: msg.content || msg.message,
            sender_name: msg.sender_name || 'Usuário'
          }))
        : [];
        
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      setMessages([]);
    }
  };

  // Função para upload de arquivos
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsUploadingFile(true);
    try {
      console.log('📎 Fazendo upload de arquivos:', files.map(f => f.name));
      // Upload real para Supabase Storage
      const uploadedFiles = await Promise.all(
        files.map(async (file) => {
          const uploadResult = await Integrations.UploadFile(file, 'uploads');
          if (!uploadResult || !uploadResult.path) throw new Error('Falha ao fazer upload do arquivo');
          return {
            id: Date.now() + Math.random(),
            name: file.name,
            size: file.size,
            type: file.type,
            url: uploadResult.path, // path do Storage
            uploaded_at: new Date()
          };
        })
      );
      setAttachments(prev => [...prev, ...uploadedFiles]);
      console.log('✅ Arquivos carregados:', uploadedFiles);
    } catch (error) {
      console.error('❌ Erro ao fazer upload:', error);
      alert(error.message || 'Erro ao fazer upload do arquivo');
    } finally {
      setIsUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Função para remover anexo
  const handleRemoveAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  // Função para adicionar comentário com anexos
  const handleAddComment = async () => {
    if ((!editorHtml || editorHtml === '<p><br></p>') && attachments.length === 0) return;
    
    setIsAddingComment(true);
    try {
      console.log('💬 Adicionando comentário:', {
        content: editorHtml,
        user: currentUser?.name,
        ticketId,
        attachments: attachments.length,
        isInternal
      });
      
      // Salvar comentário na tabela ticket_comments
      const commentPayload = {
        ticket_id: ticketId,
        user_id: currentUser?.id,
        content: editorHtml || '[Anexo adicionado]',
        is_internal: isInternal,
        is_solution: false,
        attachments: attachments,
        time_spent: 0
      };
      
      console.log('💾 Salvando comentário:', commentPayload);
      
      const savedComment = await TicketComment.create(commentPayload);
      
      console.log('✅ Comentário salvo:', savedComment);
      
      // Limpar formulário
      setEditorHtml('');
      setAttachments([]);
      setIsInternal(false);
      
      // Recarregar atividades
      await loadActivities();
      
      console.log('✅ Comentário adicionado com sucesso');
      
    } catch (error) {
      console.error('❌ Erro ao adicionar comentário:', error);
    } finally {
      setIsAddingComment(false);
    }
  };

  // Função para atualizar status
  const handleStatusChange = async (status) => {
    if ((status === 'resolved' || status === 'cancelled') && !reason) {
      setPendingStatus(status);
      setShowReasonModal(true);
      return;
    }
    if (status === ticket.status || isUpdating) return;
    
    setIsUpdating(true);
    try {
      console.log('🔄 Alterando status:', { from: ticket.status, to: status });
      
      // Atualizar ticket
      await Ticket.update(ticketId, { status });
      
      // Criar atividade na tabela ticket_comments
      const activityPayload = {
        ticket_id: ticketId,
        user_id: currentUser?.id,
        content: `Status alterado de "${STATUS_CONFIG[ticket.status]?.label}" para "${STATUS_CONFIG[status]?.label}"` + (reason ? `<br><b>Motivo/Solução:</b> ${reason}` : ''),
        is_internal: true,
        is_solution: false,
        attachments: [],
        time_spent: 0
      };
      
      await TicketComment.create(activityPayload);
      
      // Atualizar estado local
      setTicket(prev => ({ ...prev, status }));
      setNewStatus(status);
      setReason('');
      setShowReasonModal(false);
      setPendingStatus(null);
      
      // Recarregar atividades
      await loadActivities();
      
      console.log('✅ Status alterado com sucesso');
      
    } catch (error) {
      console.error('❌ Erro ao alterar status:', error);
      alert('Erro ao alterar status: ' + (error.message || error));
    } finally {
      setIsUpdating(false);
    }
  };

  // Função para atualizar prioridade
  const handlePriorityChange = async (priority) => {
    if (priority === ticket.priority || isUpdating) return;
    
    setIsUpdating(true);
    try {
      console.log('🔄 Alterando prioridade:', { from: ticket.priority, to: priority });
      
      // Atualizar ticket
      await Ticket.update(ticketId, { priority });
      
      // Criar atividade
      const activityPayload = {
        ticket_id: ticketId,
        user_id: currentUser?.id,
        content: `Prioridade alterada de "${PRIORITY_CONFIG[ticket.priority]?.label}" para "${PRIORITY_CONFIG[priority]?.label}"`,
        is_internal: true,
        is_solution: false,
        attachments: [],
        time_spent: 0
      };
      
      await TicketComment.create(activityPayload);
      
      // Atualizar estado local
      setTicket(prev => ({ ...prev, priority }));
      setNewPriority(priority);
      
      // Recarregar atividades
      await loadActivities();
      
      console.log('✅ Prioridade alterada com sucesso');
      
    } catch (error) {
      console.error('❌ Erro ao alterar prioridade:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Função para atualizar responsável
  const handleAssigneeChange = async (assigneeId) => {
    // Impedir alteração de responsável em tickets concluídos
    if (isTicketCompleted) {
      console.log('❌ Não é possível alterar responsável de tickets concluídos');
      return;
    }
    
    if (assigneeId === ticket.assigned_to || isUpdating) return;
    
    setIsUpdating(true);
    try {
      console.log('🔄 Alterando responsável:', { from: ticket.assigned_to, to: assigneeId });
      
      // Atualizar ticket
      await Ticket.update(ticketId, { assigned_to: assigneeId || null });
      
      // Buscar dados do novo responsável
      let newAssigneeName = 'Não atribuído';
      let assigneeData = null;
      if (assigneeId) {
        assigneeData = await UserEntity.findById(assigneeId);
        newAssigneeName = assigneeData?.name || 'Usuário';
        setAssignee(assigneeData);
        
        // Enviar webhook se o usuário tiver telefone
        if (assigneeData?.phone) {
          try {
            console.log('📱 Enviando notificação via webhook para:', assigneeData.phone);
            
            const webhookPayload = {
              ticket_id: ticketId,
              ticket_title: ticket.title,
              ticket_description: ticket.description,
              assignee_name: assigneeData.name,
              assignee_email: assigneeData.email,
              assignee_phone: assigneeData.phone,
              assigned_by: currentUser?.name || 'Sistema',
              assigned_at: new Date().toISOString(),
              priority: ticket.priority,
              status: ticket.status
            };
            
            const response = await fetch('https://eoydy9430qifb0q.m.pipedream.net', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(webhookPayload)
            });
            
            if (response.ok) {
              console.log('✅ Webhook enviado com sucesso');
            } else {
              console.warn('⚠️ Falha ao enviar webhook:', response.status, response.statusText);
            }
          } catch (webhookError) {
            console.error('❌ Erro ao enviar webhook:', webhookError);
            // Não interromper o fluxo principal por erro no webhook
          }
        } else {
          console.log('📵 Usuário não possui telefone cadastrado, webhook não enviado');
        }
      } else {
        setAssignee(null);
      }
      
      // Criar atividade
      const oldAssigneeName = assignee?.name || 'Não atribuído';
      const activityPayload = {
        ticket_id: ticketId,
        user_id: currentUser?.id,
        content: `Responsável alterado de "${oldAssigneeName}" para "${newAssigneeName}"`,
        is_internal: true,
        is_solution: false,
        attachments: [],
        time_spent: 0
      };
      
      await TicketComment.create(activityPayload);
      
      // Atualizar estado local
      setTicket(prev => ({ ...prev, assigned_to: assigneeId }));
      setNewAssignee(assigneeId);
      
      // Recarregar atividades
      await loadActivities();
      
      console.log('✅ Responsável alterado com sucesso');
      
    } catch (error) {
      console.error('❌ Erro ao alterar responsável:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Substituir o handlePasteComment para inserir <img src="..." /> no texto
  const handlePasteComment = async (e) => {
    if (e.clipboardData && e.clipboardData.items) {
      for (let i = 0; i < e.clipboardData.items.length; i++) {
        const item = e.clipboardData.items[i];
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) {
            setIsUploadingFile(true);
            try {
              const uploadResult = await Integrations.UploadFile(file, 'uploads');
              if (!uploadResult || !uploadResult.path) throw new Error('Falha ao fazer upload da imagem colada');
              // Inserir <img src="..." /> no local do cursor
              const url = uploadResult.path;
              const textarea = commentRef.current;
              if (textarea) {
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const before = newComment.substring(0, start);
                const after = newComment.substring(end);
                const imgTag = `\n<img src=\"${url}\" alt=\"imagem-colada\" style=\"max-width:300px;max-height:200px;border-radius:8px;margin:8px 0;\" />\n`;
                setNewComment(before + imgTag + after);
                // Reposicionar o cursor após a imagem
                setTimeout(() => {
                  textarea.selectionStart = textarea.selectionEnd = (before + imgTag).length;
                  textarea.focus();
                }, 0);
              } else {
                setNewComment(prev => prev + `\n<img src=\"${url}\" alt=\"imagem-colada\" style=\"max-width:300px;max-height:200px;border-radius:8px;margin:8px 0;\" />\n`);
              }
            } catch (error) {
              alert('Erro ao fazer upload da imagem colada.');
            }
            setIsUploadingFile(false);
          }
        }
      }
    }
  };

  // Função para abrir modal de visualização de imagem
  const handleImageView = (imageUrl, imageName) => {
    console.log('🖼️ handleImageView chamada:', { imageUrl, imageName });
    setCurrentImageUrl(imageUrl);
    setCurrentImageName(imageName);
    setIsImageViewerOpen(true);
  };

  // Função para baixar imagem
  const handleImageDownload = async (imageUrl, imageName) => {
    console.log('⬇️ handleImageDownload chamada:', { imageUrl, imageName });
    try {
      let downloadUrl = imageUrl;
      let blob;
      
      // Se é uma imagem base64 (data:image/...)
      if (imageUrl.startsWith('data:image/')) {
        console.log('📷 Convertendo imagem base64 para download');
        const response = await fetch(imageUrl);
        blob = await response.blob();
      }
      // Se é uma URL do Supabase Storage (público ou signed), usar diretamente
      else if (imageUrl.includes('supabase.co/storage/v1/object/')) {
        console.log('🌐 URL do Supabase detectada');
        downloadUrl = imageUrl;
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error('Falha ao baixar a imagem');
        }
        blob = await response.blob();
      }
      // Se não é uma URL completa, tentar gerar signed URL
      else if (!imageUrl.startsWith('http')) {
        console.log('🔗 Gerando signed URL para:', imageUrl);
        downloadUrl = await getSignedUrl(imageUrl);
        if (!downloadUrl) {
          throw new Error('Não foi possível gerar o link de download.');
        }
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error('Falha ao baixar a imagem');
        }
        blob = await response.blob();
      }
      // URLs externas
      else {
        console.log('🌐 URL externa detectada');
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error('Falha ao baixar a imagem');
        }
        blob = await response.blob();
      }
      
      console.log('📥 Criando link de download');
      const url = window.URL.createObjectURL(blob);
      
      // Determinar nome e extensão do arquivo
      let fileName = imageName || 'imagem';
      if (!fileName.includes('.')) {
        // Tentar determinar extensão pelo tipo MIME
        if (blob.type) {
          const extension = blob.type.split('/')[1];
          fileName += `.${extension}`;
        } else if (downloadUrl && downloadUrl !== imageUrl) {
          // Tentar extrair da URL
          const urlParts = downloadUrl.split('/');
          const lastPart = urlParts[urlParts.length - 1];
          if (lastPart.includes('.')) {
            const fileNameFromUrl = decodeURIComponent(lastPart.split('?')[0]);
            const extension = fileNameFromUrl.split('.').pop();
            fileName += `.${extension}`;
          } else {
            fileName += '.png'; // fallback
          }
        } else {
          fileName += '.png'; // fallback
        }
      }
      
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Limpar o objeto URL
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Download concluído:', fileName);
    } catch (error) {
      console.error('❌ Erro no download:', error);
      alert(error.message || 'Erro ao baixar imagem.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando ticket...</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Ticket não encontrado</h1>
          <p className="text-gray-600 mb-4">O ticket solicitado não existe ou você não tem permissão para visualizá-lo.</p>
          <Button onClick={() => navigate(createPageUrl('Tickets'))}>
            Voltar para Tickets
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(createPageUrl('Tickets'))}
              >
                ← Voltar
              </Button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  Ticket #{ticket.ticket_number}
                </h1>
                <p className="text-sm text-gray-500">{ticket.title}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Badge 
                className={`${STATUS_CONFIG[ticket.status]?.bgColor} ${STATUS_CONFIG[ticket.status]?.textColor} border-0`}
              >
                {STATUS_CONFIG[ticket.status]?.label}
              </Badge>
              <Badge 
                className={`${PRIORITY_CONFIG[ticket.priority]?.bgColor} ${PRIORITY_CONFIG[ticket.priority]?.textColor} border-0`}
              >
                {PRIORITY_CONFIG[ticket.priority]?.label}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Coluna Principal - Descrição e Atividades */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Descrição do Ticket */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Descrição
                  <Badge variant="outline" className="ml-auto">
                    {ticket.call_type === 'servico' ? '🛠️ Serviço' : '🖥️ Produto'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {ticket.description || 'Nenhuma descrição fornecida.'}
                  </p>
                  
                  {/* Informações adicionais baseadas no tipo */}
                  {ticket.call_type === 'servico' && ticket.service_description && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">📋 Descrição do Serviço Solicitado</h4>
                      <p className="text-sm text-blue-700 whitespace-pre-wrap">
                        {ticket.service_description}
                      </p>
                    </div>
                  )}
                  
                  {ticket.call_type === 'produto' && (system || systemModule) && (
                    <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-md">
                      <h4 className="text-sm font-medium text-orange-800 mb-2">⚙️ Sistema Afetado</h4>
                      <div className="space-y-1">
                        {system && (
                          <div className="text-sm">
                            <span className="text-orange-700 font-medium">Sistema:</span>
                            <span className="text-orange-600 ml-2">
                              {system.name} {system.version && `(v${system.version})`}
                            </span>
                          </div>
                        )}
                        {systemModule && (
                          <div className="text-sm">
                            <span className="text-orange-700 font-medium">Módulo:</span>
                            <span className="text-orange-600 ml-2">
                              {systemModule.name}
                              {systemModule.is_core && <span className="text-orange-500 ml-1">(Core)</span>}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Timeline de Atividades */}
            <Card>
              <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Atividades
                <Badge variant="secondary" className="ml-auto">
                  {activities.length}
                </Badge>
              </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-96">
                  <div className="space-y-1" ref={activitiesRef}>
                    <AnimatePresence>
                      {activities.map((activity, index) => (
                        <motion.div
                          key={activity.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                        >
                          <ActivityItem 
                            activity={activity} 
                            isLast={index === activities.length - 1}
                            onImageView={handleImageView}
                            onImageDownload={handleImageDownload}
                          />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                    
                    {activities.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>Nenhuma atividade registrada ainda.</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Aviso de Ticket Concluído */}
            {isTicketCompleted && (
              <Card className="border-yellow-200 bg-yellow-50">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                    <div>
                      <h3 className="text-sm font-medium text-yellow-800">Chamado Concluído</h3>
                      <p className="text-sm text-yellow-700 mt-1">
                        Este chamado foi concluído e não permite mais alterações ou comentários.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Adicionar Comentário */}
            {!isTicketCompleted && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Adicionar Comentário
                  </CardTitle>
                </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <ReactQuill
                    theme="snow"
                    value={editorHtml}
                    onChange={setEditorHtml}
                    placeholder={isTicketCompleted ? "Não é possível adicionar comentários em tickets concluídos" : "Digite seu comentário... (Você pode colar imagens e misturar com texto)"}
                    readOnly={isTicketCompleted}
                    modules={{
                      toolbar: isTicketCompleted ? false : [
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        ['link', 'image'],
                        ['clean']
                      ]
                    }}
                    style={{ minHeight: 120, marginBottom: 16 }}
                  />
                  <div className="flex justify-between items-center mt-2 text-sm text-gray-500">
                    <span>{editorHtml.length}/1000 caracteres</span>
                  </div>
                </div>

                {/* Anexos */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium">Anexos</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploadingFile || isTicketCompleted}
                      className="h-8"
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      {isUploadingFile ? 'Enviando...' : 'Anexar'}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      accept="*/*"
                    />
                  </div>

                  {attachments.length > 0 && (
                    <div className="space-y-2">
                      {attachments.map((attachment) => (
                        <div key={attachment.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                          {attachment.type && attachment.type.startsWith('image') ? (
                            <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                              <img src={attachment.url} alt={attachment.name} className="h-10 w-10 object-cover rounded border" />
                            </a>
                          ) : (
                            <Paperclip className="h-4 w-4 text-gray-500" />
                          )}
                          <span className="text-sm text-gray-700 flex-1">{attachment.name}</span>
                          <span className="text-xs text-gray-500">
                            {(attachment.size / 1024).toFixed(1)} KB
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveAttachment(attachment.id)}
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Opções do comentário */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isInternal"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      className="rounded"
                    />
                    <Label htmlFor="isInternal" className="text-sm">
                      Comentário interno (não visível para o cliente)
                    </Label>
                  </div>
                  
                  <Button
                    onClick={handleAddComment}
                    disabled={(!editorHtml || editorHtml === '<p><br></p>') && attachments.length === 0 || isAddingComment || isTicketCompleted}
                    className="min-w-[120px]"
                  >
                    {isAddingComment ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Adicionar
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
            )}
          </div>

          {/* Sidebar - Informações do Ticket */}
          <div className="space-y-6">
            
            {/* Informações Básicas */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Informações</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                
                {/* Status */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Status</Label>
                  <Select value={newStatus} onValueChange={handleStatusChange} disabled={isUpdating || isTicketCompleted}>
                    <SelectTrigger>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${STATUS_CONFIG[newStatus]?.color}`} />
                        {STATUS_CONFIG[newStatus]?.label || newStatus || 'Selecione'}
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${config.color}`} />
                            {config.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Prioridade */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Prioridade</Label>
                  <Select value={newPriority} onValueChange={handlePriorityChange} disabled={isUpdating || isTicketCompleted}>
                    <SelectTrigger>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${PRIORITY_CONFIG[newPriority]?.color}`} />
                        {PRIORITY_CONFIG[newPriority]?.label || newPriority || 'Selecione'}
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${config.color}`} />
                            {config.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                {/* Solicitante */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Solicitante</Label>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={requester?.avatar_url} />
                      <AvatarFallback>
                        {(requester?.name || 'U').substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{requester?.name || 'Não informado'}</p>
                      <p className="text-xs text-gray-500">{requester?.email}</p>
                    </div>
                  </div>
                </div>

                {/* Responsável */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Responsável</Label>
                  {assignee ? (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={assignee?.avatar_url} />
                        <AvatarFallback>
                          {(assignee?.name || 'U').substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{assignee?.name}</p>
                        <p className="text-xs text-gray-500">{assignee?.email}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Não atribuído</p>
                  )}
                </div>

                <Separator />

                {/* Empresa */}
                {company && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Empresa</Label>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium">{company.name || company.company_name}</p>
                        {company.email && (
                          <p className="text-xs text-gray-500">{company.email}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <Separator />

                {/* Detalhes Técnicos */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-700">Detalhes Técnicos</Label>
                  
                  {/* Tipo de Chamado */}
                  <div className="flex items-center gap-2 text-sm">
                    <div className="flex items-center gap-1">
                      {ticket.call_type === 'servico' ? '🛠️' : '🖥️'}
                      <span className="text-gray-700">Tipo:</span>
                    </div>
                    <span className="text-gray-600 font-medium">
                      {ticket.call_type === 'servico' ? 'Serviço (Solicitação)' : 'Produto (Problema em Sistema)'}
                    </span>
                  </div>

                  {/* Campos condicionais para PRODUTO */}
                  {ticket.call_type === 'produto' && (
                    <>
                      {/* Sistema */}
                      {system && (
                        <div className="flex items-start gap-2 text-sm">
                          <span className="text-gray-700 mt-0.5">Sistema:</span>
                          <div className="flex-1">
                            <p className="text-gray-600 font-medium">
                              {system.name} {system.version && `(v${system.version})`}
                            </p>
                            {system.description && (
                              <p className="text-xs text-gray-500 mt-0.5">{system.description}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Módulo */}
                      {systemModule && (
                        <div className="flex items-start gap-2 text-sm">
                          <span className="text-gray-700 mt-0.5">Módulo:</span>
                          <div className="flex-1">
                            <div className="text-gray-600 font-medium">
                              {systemModule.name}
                              {systemModule.is_core && (
                                <Badge variant="secondary" className="ml-2 text-xs">Core</Badge>
                              )}
                            </div>
                            {systemModule.description && (
                              <p className="text-xs text-gray-500 mt-0.5">{systemModule.description}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-0.5 capitalize">
                              Tipo: {systemModule.module_type}
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Campo condicional para SERVIÇO */}
                  {ticket.call_type === 'servico' && ticket.service_description && (
                    <div className="flex items-start gap-2 text-sm">
                      <span className="text-gray-700 mt-0.5">Serviço:</span>
                      <div className="flex-1">
                        <p className="text-gray-600 text-sm whitespace-pre-wrap">
                          {ticket.service_description}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Datas */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span className="text-gray-700">Criado:</span>
                    <span className="text-gray-600">
                      {formatDateSafely(ticket.created_at)}
                    </span>
                  </div>
                  
                  {ticket.due_date && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-700">Prazo:</span>
                      <span className="text-gray-600">
                        {formatDateSafely(ticket.due_date)}
                      </span>
                    </div>
                  )}
                  
                  {ticket.resolved_at && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4 text-green-500" />
                      <span className="text-gray-700">Resolvido:</span>
                      <span className="text-gray-600">
                        {formatDateSafely(ticket.resolved_at)}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Resumo de Mensagens */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Chat
                  <Badge variant="secondary" className="ml-auto">
                    {messages.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {messages.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      {messages.length} mensagem{messages.length !== 1 ? 's' : ''} no chat
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(createPageUrl('Chat'))}
                      className="w-full"
                    >
                      Abrir Chat
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Nenhuma mensagem no chat</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Modal para motivo/solução */}
      {showReasonModal && (
        <Dialog open={showReasonModal} onOpenChange={setShowReasonModal}>
          <DialogContent className="max-w-2xl w-full">
            <DialogHeader>
              <DialogTitle>Informe o motivo/solução</DialogTitle>
            </DialogHeader>
            <div style={{ minHeight: 180, marginBottom: 16, maxWidth: '100%' }}>
              <ReactQuill
                theme="snow"
                value={reason}
                onChange={setReason}
                placeholder="Descreva o motivo ou solução... (Você pode colar imagens e misturar com texto)"
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['link', 'image'],
                    ['clean']
                  ]
                }}
                style={{
                  minHeight: 140,
                  maxHeight: 320,
                  width: '100%',
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-line',
                  overflowWrap: 'break-word',
                  overflowY: 'auto'
                }}
              />
            </div>
            <DialogFooter>
              <Button
                onClick={async () => {
                  if (!reason || reason === '<p><br></p>') return alert('Informe o motivo ou solução!');
                  await handleStatusChange(pendingStatus);
                }}
              >Salvar</Button>
              <DialogClose asChild>
                <Button variant="ghost" onClick={() => { setShowReasonModal(false); setReason(''); setPendingStatus(null); }}>Cancelar</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal de visualização de imagem */}
      {isImageViewerOpen && (
        <ImageViewerModal
          isOpen={isImageViewerOpen}
          onClose={() => setIsImageViewerOpen(false)}
          imageUrl={currentImageUrl}
          imageName={currentImageName}
          onDownload={handleImageDownload}
        />
      )}
    </div>
  );
}